import { openDB, type DBSchema, type IDBPDatabase } from "idb";

import { QUOTATION_SCHEMA_VERSION, type Quotation } from "./model";

const DATABASE_NAME = "quotation-and-qr-sender";
const DATABASE_VERSION = 1;
const DRAFT_STORE = "quotation-drafts";

interface QuotationDraftDatabase extends DBSchema {
  "quotation-drafts": {
    key: string;
    value: Quotation;
    indexes: {
      "by-updated-at": string;
    };
  };
}

let databasePromise: Promise<IDBPDatabase<QuotationDraftDatabase>> | undefined;

function getDatabase(): Promise<IDBPDatabase<QuotationDraftDatabase>> {
  databasePromise ??= openDB<QuotationDraftDatabase>(DATABASE_NAME, DATABASE_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(DRAFT_STORE)) {
        const store = database.createObjectStore(DRAFT_STORE, {
          keyPath: "id",
        });
        store.createIndex("by-updated-at", "updatedAt");
      }
    },
    blocking() {
      void closeQuotationDraftDatabase();
    },
    terminated() {
      databasePromise = undefined;
    },
  });

  return databasePromise;
}

export async function closeQuotationDraftDatabase(): Promise<void> {
  const pendingDatabase = databasePromise;
  databasePromise = undefined;
  if (pendingDatabase === undefined) {
    return;
  }
  const database = await pendingDatabase;
  database.close();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isPersistedQuotation(value: unknown): value is Quotation {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.schemaVersion === QUOTATION_SCHEMA_VERSION &&
    typeof value.id === "string" &&
    typeof value.createdAt === "string" &&
    typeof value.updatedAt === "string" &&
    typeof value.currency === "string" &&
    isRecord(value.seller) &&
    isRecord(value.customer) &&
    Array.isArray(value.lineItems) &&
    Array.isArray(value.adjustments) &&
    Array.isArray(value.terms) &&
    isRecord(value.templateContext)
  );
}

export async function saveQuotationDraft(quotation: Quotation): Promise<void> {
  if (!isPersistedQuotation(quotation)) {
    throw new TypeError("Only a valid quotation can be stored as a draft.");
  }

  const database = await getDatabase();
  await database.put(DRAFT_STORE, structuredClone(quotation));
}

export async function getQuotationDraft(id: string): Promise<Quotation | undefined> {
  const database = await getDatabase();
  const value: unknown = await database.get(DRAFT_STORE, id);
  return isPersistedQuotation(value) ? structuredClone(value) : undefined;
}

export async function listQuotationDrafts(): Promise<Quotation[]> {
  const database = await getDatabase();
  const values: unknown[] = await database.getAll(DRAFT_STORE);

  return values
    .filter(isPersistedQuotation)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
    .map((quotation) => structuredClone(quotation));
}

export async function deleteQuotationDraft(id: string): Promise<void> {
  const database = await getDatabase();
  await database.delete(DRAFT_STORE, id);
}
